import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import ChatMessages from "./Messages/messages";
import Header from "../../Header/Header";
import { useParams, useSearchParams } from "react-router-dom";
import ChatStore from "../../../store/chatStore";
import "../../../assets/css/styles.css";

import { FileStore } from "../../../store/fileUploadStore";
import UIStore from "../../../store/uiStore";

import { LightPdf } from "../../../assets";
import { useChatInfo, useSpectraById, useSpectraOwner, useSpectraSparks, useChatMessages, useSendMessage, useRegenerateMessage } from "../../../api/queryHooks";
import { useQueryClient } from "@tanstack/react-query";
import Loader from "../../Loader/Loader";
import PromptStore, { getDisplayAnswerId } from "../../../store/promptStore";
import UserAuthStore from "../../../store/userStore";
import { ALLOWED_FILE_ACCEPT_STRING } from "../../../utils/helpers";
import { apiUrl } from "../../../constants/const";
import { invokeCloudRun } from "../../../api/invoke";
import { handleSend } from "../../../api/generate";
import { createMessageLoadingHandlers } from "../../../api/generate/useMessageLoading";
import { prewarmChatData } from "../../../utils/sparkApi/getCacheandFileData";

import ScrollToBottomStore from "../../../store/ScrollToBottomStore";
import useGenerateApiSignal from "../../../store/useGenerateApiSignal";
import useStreamingStateStore from "../../../store/useStreamingStateStore";
import { useGetAIModels } from "../../../api/queryHooks";

import SparkLayout from "../SparkLayout";
import Prompt from "../../Prompt/Prompt";
import NebulaMessageDots from "../../Nebula/NebulaMessageDots";
import NebulaCanvas from "../../Nebula/NebulaCanvas";
import NebulaActivityPanel from "../../Nebula/NebulaActivityPanel";
import NebulaOverview from "../../Nebula/NebulaOverview";
import NebulaApprovalPanel from "../../Nebula/NebulaApprovalPanel";
import { parseNebulaResponse } from "../../../utils/nebulaParser";
import MessageFooter from "../../shared/Message/MessageFooter";
import regenerate from "../../../api/regenerate";
import { ChevronUp } from "lucide-react";
import { useChatListeners } from "../../../services/ListenerManager/useChatListeners";
import { useSpectraListeners } from "../../../services/ListenerManager/useSpectraListeners";

import { useNewChatRedirect } from "../../../hooks/chat/useNewChatRedirect";
import { useChatStateReset } from "../../../hooks/chat/useChatStateReset";
import { useIsNewChat } from "../../../hooks/chat/useIsNewChat";
import { isEmptyChatRefetching } from "./chatLoadingState";
import { useApprovalFlow } from "../../../hooks/chat/useApprovalFlow";
import { useMessageHandlers } from "../../../hooks/chat/useMessageHandlers";
import { useFileHandling } from "../../../hooks/chat/useFileHandling";
import { useReconnectStream } from "../../../hooks/chat/useReconnectStream";
import { useRefreshRecovery } from "../../../hooks/chat/useRefreshRecovery";
import SparkContext, { useSparkContext } from "../../../context/SparkContext";
import useDesktopDeviceStore from "../../../store/useDesktopDeviceStore";
import useFeedbackStore from "../../../store/useFeedbackStore";
import {
  canRetryAnswer,
  isStatusForActiveAnswer,
  isTerminalAnswerStatus,
  shouldShowMessageActions,
} from "@photon/chat-core";

// Consumed inside the SparkContext.Provider so useChatListeners can read
// chat_id and space from context instead of requiring explicit props.
const ChatListenerBridge = () => {
  const { chat_id, space, user_id } = useSparkContext();
  // For private sparks (no space), fall back to current user as the doc owner
  const effectiveSpace = space || { owner_id: user_id };
  useChatListeners(chat_id, effectiveSpace);
  return null;
};

// Subscribes to spectra real-time listeners (new sparks, renames, etc.) so the
// sidebar stays in sync without requiring the user to reload.
const SpectraListenerBridge = () => {
  const { space } = useSparkContext();
  useSpectraListeners(space);
  return null;
};

// Stable empty array for Zustand selectors to avoid creating a new reference each render
const EMPTY_ACTIVITY_ITEMS = [];

const MainContent = ({ space_id }) => {
  const chat_id = useParams().id;
  const [searchParams, setSearchParams] = useSearchParams();
  const isNebulaMode = searchParams.get("mode") === "nebula";
  const [compactAnswers, setCompactAnswers] = useState(true);
  const [expandedExchangeIds, setExpandedExchangeIds] = useState(() => new Set());
  const [collapsedExchangeIds, setCollapsedExchangeIds] = useState(() => new Set());

  useEffect(() => {
    setExpandedExchangeIds(new Set());
    setCollapsedExchangeIds(new Set());
  }, [chat_id]);

  const setExchangeExpanded = useCallback((exchangeId, expanded) => {
    if (!exchangeId) return;
    setExpandedExchangeIds((current) => {
      const next = new Set(current);
      if (expanded) next.add(exchangeId);
      else next.delete(exchangeId);
      return next;
    });
    setCollapsedExchangeIds((current) => {
      const next = new Set(current);
      if (expanded) next.delete(exchangeId);
      else next.add(exchangeId);
      return next;
    });
  }, []);

  const changeCompactAnswers = useCallback((enabled) => {
    setCompactAnswers(enabled);
    setExpandedExchangeIds(new Set());
    setCollapsedExchangeIds(new Set());
  }, []);
  // Lazily build the Nebula canvas only after the user first enters it, then
  // keep it mounted (hidden) so toggling back and forth is instant instead of
  // unmounting/remounting the heavy editor + preview each time.
  const [hasEnteredNebula, setHasEnteredNebula] = useState(isNebulaMode);
  useEffect(() => {
    if (isNebulaMode) setHasEnteredNebula(true);
  }, [isNebulaMode]);
  const user = UserAuthStore((state) => state.user);
  const orgId = UserAuthStore((state) => state.orgId);

  // Track how many Nebula artifacts the user last acknowledged per chat.
  // Dot shows when nebulaMessages.length > lastSeenCount (new artifacts since last Nebula visit).
  const [lastSeenCount, setLastSeenCount] = useState(() => {
    const stored = localStorage.getItem(`nebula_seen_count_${chat_id}`);
    return stored ? parseInt(stored, 10) : 0;
  });

  // Reset lastSeenCount when switching chats
  useEffect(() => {
    const stored = localStorage.getItem(`nebula_seen_count_${chat_id}`);
    setLastSeenCount(stored ? parseInt(stored, 10) : 0);
  }, [chat_id]);

  // Reset scroll to top whenever entering Nebula mode — chat-mode scroll
  // position would otherwise leave the Nebula header off-screen and force
  // the user to scroll up to see it. The chat transcript now stays mounted in
  // the same scroll container (instead of remounting), so we save the chat
  // scroll position on entry as a fallback for the exit effect below (used
  // only if the message being viewed in Nebula can't be located in the DOM).
  const chatScrollTop = useRef(0);
  useEffect(() => {
    const el = document.getElementById("spark-scroll-container");
    if (!el) return;
    if (isNebulaMode) {
      chatScrollTop.current = el.scrollTop;
      el.scrollTo({ top: 0 });
    }
  }, [isNebulaMode]);
  
  // Lightweight owner resolution — single database read, no file/TOC data.
  // Used to unblock useChatInfo/useChatMessages without waiting for the full space doc.
  const { data: ownerData, isLoading: isLoadingOwner } = useSpectraOwner(space_id, orgId, user?.uid, {
    enabled: !!space_id && !!user?.uid && !!orgId,
  });
  const spaceOwnerId = ownerData?.owner_id ?? null;

  // Fetch full space data for header, listeners, and sparks sidebar pre-warm.
  // This is NOT in the chat loading critical path — chat queries gate on ownerData above.
  const { data: spaceResponse } = useSpectraById(space_id, orgId, user?.uid, {
    enabled: !!space_id && !!user?.uid && !!orgId,
  });

  // Pre-warm the spectra-sparks cache for the active space so the sidebar
  // accordion renders immediately and real-time invalidations propagate even
  // before the user has clicked the accordion to expand it.
  useSpectraSparks(
    space_id,
    orgId,
    user?.uid,
    spaceOwnerId,
    { enabled: !!space_id && !!orgId && !!user?.uid && !!spaceOwnerId }
  );
  
  const setActiveChat = ChatStore((state) => state.setActiveChat);
  const setTempChatId = ChatStore((state) => state.setTempChatId);
  const setAddChatDisabled = ChatStore((state) => state.setAddChatDisabled);
  const setIsLatestMessageLoading = ChatStore((state) => state.setIsLatestMessageLoading);
  const isLatestMessageLoading = ChatStore((state) => state.isLatestMessageLoading);
  
  const modeRef = UIStore((state) => state.modeRef);
  const showSidebar = UIStore((state) => state.showSidebar);
  const setShowSidebar = UIStore((state) => state.setShowSidebar);
  const setSuppressSidebarAnimation = UIStore((state) => state.setSuppressSidebarAnimation);
  
  const files = FileStore((state) => state.files);
  const setFiles = FileStore((state) => state.setFiles);
  const setNewFiles = FileStore((state) => state.setNewFiles);
  
  const setShowScrollButton = ScrollToBottomStore((state) => state.setShowScrollButton);
  
  const [messageCount, setMessageCount] = useState(0);
  const setPrompt = PromptStore((state) => state.setPrompt);
  
  // Lifted state from Messages.js (messages now read directly from React Query)
  const [isMessagesRendered, setMessagesRendered] = useState(false);
  const { data: modelsData } = useGetAIModels(); // shared React Query cache — no extra network request
  const defaultModel = modelsData?.default_llm_id?.id ?? null;
  const [regenerateIndex, setRegenerateIndex] = useState(null);
  const [editedIndex, setEditedIndex] = useState({});
  const [generateStatus, setGenerateStatus] = useState(null);
  const [nebulaActiveIndex, setNebulaActiveIndex] = useState(null);
  const [nebulaFooterOpen, setNebulaFooterOpen] = useState(false);
  const [nebulaViewMode, setNebulaViewMode] = useState("canvas");

  const messageContainerRef = useRef(null);

  const selectedAnswerByPrompt = PromptStore((state) => state.selectedAnswerByPrompt);
  const setSelectedAnswer = PromptStore((state) => state.setSelectedAnswer);
  const reconcileSelectedAnswer = PromptStore((state) => state.reconcileSelectedAnswer);

  // NOTE: view mode is intentionally NOT reset on nebulaActiveIndex change.
  // The active index auto-advances to the latest message during generation
  // (see effect below), and we want users in cluster (overview) mode to stay
  // there while a new artifact streams in. Explicit user navigation (dot or
  // thumbnail click) sets canvas mode at the call site.

  // Keyboard shortcuts for overview toggle (Nebula mode only)
  useEffect(() => {
    if (!isNebulaMode) return;
    const onKeyDown = (e) => {
      // Skip when focus is inside any text input
      const tag = document.activeElement?.tagName;
      const isEditable = tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable;
      if (isEditable) return;

      if (e.key === "g" || e.key === "G") {
        setNebulaViewMode(v => v === "overview" ? "canvas" : "overview");
      } else if (e.key === "Escape") {
        setNebulaViewMode("canvas");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isNebulaMode]);

  // Build a minimal space context for chat queries — only owner_id is required
  // to construct the correct database path. Full spaceResponse is used later
  // for header / listener data once it arrives.
  const spaceContext = spaceOwnerId
    ? { owner_id: spaceOwnerId, space_id }
    : spaceResponse?.spectra ?? null;

  // Data fetching hooks — gate on the lightweight ownerData (not full spaceResponse)
  // so useChatInfo and useChatMessages fire as soon as owner_id is known.
  const { data: chat_info, isLoading: chatInfoLoading } = useChatInfo(chat_id, spaceContext, {
    enabled: !!chat_id && !!user?.uid && !!orgId && (!space_id || !!spaceContext),
  });
  const chat = chat_info;
  
  const { 
    data: messagesData,
    isLoading: messagesLoading,
    isFetching: messagesFetching,
  } = useChatMessages(chat_id, spaceContext, {
    enabled: !!user?.uid && !!chat_id && (!space_id || !!spaceContext),
  });

  // Read messages directly from React Query cache (no local state)
  // Wrapped in useMemo to prevent unnecessary re-renders in useCallback dependencies
  const messages = useMemo(() => messagesData?.messages || [], [messagesData?.messages]);
  const emptyChatRefetching = isEmptyChatRefetching({
    messagesData,
    messagesFetching,
  });

  // Read the selected device_id for this chat from the store
  const selectedDeviceId = useDesktopDeviceStore((s) => s.selectedDevices[chat_id] ?? null);

  // In Nebula mode, strip tool/activity messages — they're process steps, not content
  const nebulaMessages = useMemo(
    () => messages.filter(msg => msg.answers?.[0]?.role !== 'tool'),
    [messages]
  );

  useEffect(() => {
    nebulaMessages.forEach((message) => {
      reconcileSelectedAnswer(message.id, message.answers || []);
    });
  }, [nebulaMessages, reconcileSelectedAnswer]);

  // Jump to the latest message every time Nebula view is opened.
  useEffect(() => {
    if (isNebulaMode && nebulaMessages.length > 0) {
      setNebulaActiveIndex(nebulaMessages.length - 1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNebulaMode]);

  // While already in Nebula view, follow along as new messages arrive — but only if
  // the user was already looking at the latest one. Without the "wasAtLatest" guard,
  // a message arriving from anywhere (a collaborator's send in a Spectra spark, a
  // regenerate, etc.) force-jumped the view away from whatever older artifact the
  // user had deliberately navigated back to via onSelectIndex.
  const prevNebulaLengthRef = useRef(nebulaMessages.length);
  useEffect(() => {
    const prevLength = prevNebulaLengthRef.current;
    // Advance the ref BEFORE any early return so it never goes stale while the
    // user is outside Nebula (messages still stream in there). If we only
    // updated it inside the isNebulaMode branch, prevLength would be frozen at
    // the mount value (typically 0, since messages load after mount) and
    // wasAtLatest would compute against a stale-small prevLength — always true —
    // silently defeating this very guard the first time a message arrives.
    prevNebulaLengthRef.current = nebulaMessages.length;
    if (!isNebulaMode) return;
    const wasAtLatest = nebulaActiveIndex === null || nebulaActiveIndex >= prevLength - 1;
    if (nebulaMessages.length > prevLength && wasAtLatest) {
      setNebulaActiveIndex(nebulaMessages.length - 1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nebulaMessages.length]);

  // Mark all current Nebula artifacts as seen while in Nebula mode — clears the header dot.
  // Re-runs as new artifacts stream in so they stay marked seen while the user is viewing.
  useEffect(() => {
    if (!isNebulaMode) return;
    const count = nebulaMessages.length;
    localStorage.setItem(`nebula_seen_count_${chat_id}`, count);
    setLastSeenCount(count);
  }, [isNebulaMode, chat_id, nebulaMessages.length]);

  // Keep the toolbar reachable for empty terminal answers so Delete remains an escape hatch.
  const nebulaHasAnswer = useMemo(() => {
    if (!isNebulaMode) return false;
    const idx = nebulaActiveIndex ?? (nebulaMessages.length - 1);
    const message = nebulaMessages?.[idx];
    const selectedId = selectedAnswerByPrompt[message?.id];
    const displayId = selectedId || getDisplayAnswerId(message?.answers);
    const ans = message?.answers?.find((answer) => answer.id === displayId);
    return !!ans && (
      !!ans.content?.trim() ||
      isTerminalAnswerStatus(ans.status) ||
      ans.status === 'expired'
    );
  }, [isNebulaMode, nebulaActiveIndex, nebulaMessages, selectedAnswerByPrompt]);

  // Subscribe to activityItems for the active Nebula message — reactive so the
  // beaming label updates as SSE activity items arrive during streaming.
  // NOTE: selector must return a stable reference when no items exist, otherwise
  // Zustand sees a new [] every render and triggers an infinite update loop.
  const nebulaMsgId = nebulaMessages[nebulaActiveIndex ?? (nebulaMessages.length - 1)]?.id;
  const activeGenerationAnswerId = useGenerateApiSignal(
    (state) => state.signals[chat_id]?.answer_id
  );

  // On leaving Nebula, scroll the chat transcript to whichever message the user
  // was actually looking at (the active dot) instead of just restoring the
  // pre-Nebula scroll offset — otherwise the user could be viewing the last
  // dot (expecting to land at the bottom) or a specific message (expecting to
  // land on it) and instead resurface wherever they happened to be scrolled to
  // before ever entering Nebula, which reads as random/misaligned navigation.
  // Falls back to the pre-Nebula offset if the message isn't in the DOM
  // (e.g. it was deleted while in Nebula).
  const wasNebulaMode = useRef(isNebulaMode);
  useEffect(() => {
    if (wasNebulaMode.current && !isNebulaMode) {
      const target = nebulaMsgId && document.getElementById(`msg-${nebulaMsgId}`);
      if (target) {
        // 'center' rather than 'end': for a mid-conversation message this
        // centers it in the viewport (matches "click a dot, land on that
        // message"); for the last message, the browser naturally clamps the
        // scroll to its max (there's nothing below to center against), which
        // lands exactly at the bottom of the page (matches "last dot = bottom").
        target.scrollIntoView({ block: "center" });
      } else {
        const el = document.getElementById("spark-scroll-container");
        el?.scrollTo({ top: chatScrollTop.current });
      }
    }
    wasNebulaMode.current = isNebulaMode;
  }, [isNebulaMode, nebulaMsgId]);

  const nebulaActivityItemsRaw = useStreamingStateStore(
    (state) => state.activeStreams[nebulaMsgId]?.activityItems
  );
  const nebulaActivityItems = nebulaActivityItemsRaw || EMPTY_ACTIVITY_ITEMS;

  // Additional selectors needed for the DB-handoff guard (mirrors SingleMessage pattern).
  const nebulaIsActivelyStreaming = useStreamingStateStore(
    (state) => !!state.activeStreams[nebulaMsgId]?.streaming
  );
  const nebulaIsCompletedSnapshot = useStreamingStateStore((state) => {
    const s = state.activeStreams[nebulaMsgId];
    return !!(s && !s.streaming && (s.activityItems?.length ?? 0) > 0);
  });
  const clearCompletedSnapshot = useStreamingStateStore((state) => state.clearCompletedSnapshot);

  // DB activity for the active nebula message — derived at the top level so the
  // snapshot-clearing useEffect can reference it (can't read inside the render IIFE).
  const nebulaDbActivity = useMemo(() => {
    const msg = nebulaMessages[nebulaActiveIndex ?? (nebulaMessages.length - 1)];
    if (!msg) return null;
    const selectedId = selectedAnswerByPrompt[msg.id];
    const displayId = selectedId || getDisplayAnswerId(msg.answers);
    const ans = msg.answers?.find(a => a.id === displayId);
    return ans?.activity ?? null;
  }, [nebulaMessages, nebulaActiveIndex, selectedAnswerByPrompt]);

  const nebulaAnswer = useMemo(() => {
    const msg = nebulaMessages[nebulaActiveIndex ?? (nebulaMessages.length - 1)];
    if (!msg) return null;
    const selectedId = selectedAnswerByPrompt[msg.id];
    const displayId = selectedId || getDisplayAnswerId(msg.answers);
    return msg.answers?.find(a => a.id === displayId) ?? null;
  }, [nebulaMessages, nebulaActiveIndex, selectedAnswerByPrompt]);

  useEffect(() => {
    if (isStatusForActiveAnswer({
      answerId: nebulaAnswer?.id,
      activeAnswerId: activeGenerationAnswerId,
    })) {
      useStreamingStateStore.getState().reconcilePersistedStatus(nebulaMsgId, nebulaAnswer?.status);
    }
  }, [nebulaMsgId, nebulaAnswer?.id, nebulaAnswer?.status, activeGenerationAnswerId]);

  // Mirror of SingleMessage's snapshot-clearing guard: don't hand off to DB until it
  // has fully propagated (dbCount > 0 AND dbCount >= streamed count).
  // Prevents the blank-then-reappear flicker during the Firestore write window.
  useEffect(() => {
    const dbCount = nebulaDbActivity?.length ?? 0;
    const streamedCount = nebulaActivityItems?.length ?? 0;
    if (dbCount > 0 && dbCount >= streamedCount && nebulaIsCompletedSnapshot) {
      clearCompletedSnapshot(nebulaMsgId);
    }
  }, [nebulaDbActivity, nebulaActivityItems, nebulaIsCompletedSnapshot, nebulaMsgId, clearCompletedSnapshot]);

  // Auto-collapse sidebar on desktop when entering Nebula, restore on exit.
  // The width/margin change is made instant (suppressSidebarAnimation) so the
  // spring doesn't compete with the heavy mode-switch render; the suppression
  // is released on the next frame so normal user toggles still animate.
  const priorSidebarState = useRef(null);
  useEffect(() => {
    if (window.innerWidth < 940) return; // mobile/tablet: don't touch sidebar
    if (isNebulaMode) {
      priorSidebarState.current = showSidebar;
      setSuppressSidebarAnimation(true);
      setShowSidebar(false);
    } else {
      if (priorSidebarState.current !== null) {
        setSuppressSidebarAnimation(true);
        setShowSidebar(priorSidebarState.current);
        priorSidebarState.current = null;
      } else {
        return;
      }
    }
    const raf = requestAnimationFrame(() => setSuppressSidebarAnimation(false));
    return () => cancelAnimationFrame(raf);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNebulaMode]);

  // Get query client for cache operations during streaming
  const queryClient = useQueryClient();

  useEffect(() => {
    const oauthStatus = searchParams.get("oauth");
    if (!oauthStatus) return;

    if (oauthStatus === "success") {
      queryClient.invalidateQueries({ queryKey: ["user-details", user?.uid, orgId] });
      useFeedbackStore.getState().showFeedback("Provider connected. Try your request again.", "success");
    } else {
      useFeedbackStore.getState().showFeedback("Provider connection was not completed.", "error");
    }
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("oauth");
      next.delete("provider");
      return next;
    }, { replace: true });
  }, [orgId, queryClient, searchParams, setSearchParams, user?.uid]);

  // Mutation hooks for send/regenerate
  const sendMessageMutation = useSendMessage(chat_id, spaceResponse?.spectra?.space_id);
  const regenerateMessageMutation = useRegenerateMessage(chat_id, spaceResponse?.spectra?.space_id);

  // Derive data ready state from React Query
  const isDataReady = !chatInfoLoading && messagesData !== undefined;

  // Custom hooks for chat initialization
  useNewChatRedirect(chat_id, space_id, user);
  
  // Memoize stateSetters object since Zustand setters are stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stateSetters = useMemo(
    () => ({
      setTempChatId,
      setFiles,
      setNewFiles,
      setPrompt,
      setActiveChat,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // Empty deps because Zustand setters are stable
  );
  
  useChatStateReset(chat_id, user, stateSetters);
  
  const { isNewChat } = useIsNewChat(chat_id, user, chat_info, isDataReady);

  // Create loading handlers for approval flow
  const approvalLoadingHandlers = useMemo(() => createMessageLoadingHandlers({
    setIsLatestMessageLoading,
    setGenerateStatus,
    setRegenerateIndex,
    chat_id,
  }), [setIsLatestMessageLoading, setGenerateStatus, setRegenerateIndex, chat_id]);

  // Custom hooks for approval flow
  const {
    pendingApprovals,
    handleApprovalCardReceived,
    handleApprovalConfirm,
    handleApprovalCancel,
    handleApprovalExpire,
    handleClearApprovals,
    handleClearAllApprovals,
  } = useApprovalFlow(chat_id, user, approvalLoadingHandlers, queryClient, spaceResponse?.spectra?.space_id);

  // Custom hooks for message handlers
  const { deleteMessage, editFunctionality } = useMessageHandlers(
    chat_id,
    spaceResponse?.spectra,
    chat,
    orgId,
    user,
    {
      messages,
      setRegenerateIndex,
      setIsLatestMessageLoading,
      setAddChatDisabled,
      modeRef,
      apiUrl,
      generateStatus,
      setGenerateStatus,
      onApprovalCard: handleApprovalCardReceived,
      clearApprovals: handleClearApprovals,
      queryClient,
      deviceId: selectedDeviceId,
    }
  );

  // Initialize and cleanup (lifted from Messages.js)
  useEffect(() => {
    invokeCloudRun();

    return () => {
      setIsLatestMessageLoading(false);
      setShowScrollButton(false);
      // Capture current signal state before any mutation. signals is keyed by
      // chat_id — the store has no root-level generate_id/signal fields.
      const pendingId = useGenerateApiSignal.getState().signals[chat_id]?.generate_id;
      // Keep the generation running on the backend — just detach the local observer
      // so navigating between sub-sparks doesn't leave the textarea blocked.
      // The worker continues; the user can reattach when they return.
      useGenerateApiSignal.getState().signals[chat_id]?.signal?.abort();
      // Preserve signals[chat_id] (do NOT remove it here) if a generation was
      // started from this chat. generate_id is set immediately on send (before
      // any backend ack), so its presence is the correct signal that a generation
      // is in flight. Keeping it set across route switches ensures the sidebar
      // breathing dot stays visible on the originating spark regardless of how
      // quickly the user navigates away. Terminal events (onComplete / onFailed /
      // onCancelled) call removeSignal(chat_id) which clears it at the right time.
      if (!pendingId) {
        useGenerateApiSignal.getState().removeSignal(chat_id);
      }
      setGenerateStatus(false);
      setRegenerateIndex(null);
      // Clear any active skills when navigating away — skills are conversation-scoped
      PromptStore.getState().setActiveSkills([]);
      PromptStore.getState().clearAnswerSelections();
      // Detach the streaming observer without marking the generation as failed.
      // The worker writes a terminal event to the database when done; the
      // real-time listener will pick it up when the user returns.
      if (pendingId) {
        useStreamingStateStore.getState().detachStreaming(pendingId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat_id, user?.uid, orgId]);

  // Pre-warm database chat cache so it's ready when user hits send
  // Only fires for existing chats (chat_info.uid set) — new chats have no document yet
  useEffect(() => {
    if (chat_id && chat_info?.uid) {
      prewarmChatData(chat_id, spaceContext?.owner_id, spaceContext?.space_id);
    }
  }, [chat_id, chat_info?.uid, spaceContext?.owner_id, spaceContext?.space_id]);

  useReconnectStream({ chat_id, space_id, messages, isDataReady, apiUrl, queryClient, setIsLatestMessageLoading, setGenerateStatus });
  useRefreshRecovery({ chat_id, space_id, messages, isDataReady, apiUrl, queryClient, setIsLatestMessageLoading, setGenerateStatus });

  // Restore active skill chips from the last answer saved on this chat doc.
  // Depends on chat_id so it re-runs on navigation, and on chat_info?.uid so it
  // re-runs once the chat doc finishes loading for the new chat.
  useEffect(() => {
    if (!chat_id) return;
    const persisted = chat_info?.active_skills;
    if (Array.isArray(persisted) && persisted.length > 0) {
      PromptStore.getState().setActiveSkills(persisted);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat_id, chat_info?.uid]);

  // Track when messages first load for scroll behavior
  useEffect(() => {
    if (messagesData?.messages !== undefined) {
      if (messagesData.messages.length > 0) {
        // Scroll to bottom when messages load
        setTimeout(() => {
          const messagesContainer = document.querySelector('[data-messages-container]');
          if (messagesContainer) {
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
          }
        }, 100);
      }
    }
  }, [messagesData?.messages]);

  // Custom hooks for file handling
  const {
    isNewDrag,
    setIsNewDrag,
    fileInputRef,
    containerRef,
    handleDragEnter,
    handleDragOver,
    handleDragLeave,
    handleOnChange,
    handleOnDrop,
  } = useFileHandling(
    chat_id,
    space_id,
    user,
    orgId,
    files,
    setFiles,
    messageCount,
    isNewChat,
  );

  const isNewChatWithSpace = isNewChat && space_id;

  const sparkContextValue = {
    chat_id,
    space_id,
    space: spaceResponse?.spectra ?? null,
    isNewChat,
    isNewChatWithSpace,
    isPersisted: Boolean(chat_info?.uid),
    user_id: user?.uid ?? null,
  };

  return (
    <SparkContext.Provider value={sparkContextValue}>
    <ChatListenerBridge />
    <SpectraListenerBridge />
    <div
      ref={containerRef}
      className={`flex flex-col flex-1 h-full relative min-w-0`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleOnDrop}
    >
      {isNewDrag && (
        <div className="absolute inset-0 dark:bg-fill-hex-3f424e bg-fill-muted-control bg-opacity-75 dark:bg-opacity-75 z-[500] flex flex-col items-center justify-center pointer-events-none">
          <LightPdf className="h-[75px] w-[98px] mb-4" />
          <div className="text-ink-white text-center text-lg font-semibold">
            Drop your document here <br /> to add with spark.
          </div>
        </div>
      )}

      {chatInfoLoading || messagesLoading || emptyChatRefetching || (space_id && isLoadingOwner) || !user?.uid || !orgId ? (
        <div className="w-full h-full flex items-center justify-center">
          <Loader variant="chat" />
        </div>
      ) : chat_id ? (
        <SparkLayout
          scrollClassName={`show-scrollbar-on-hover${isNebulaMode ? " bg-fill-white dark:bg-fill-page-surface-dark" : ""}`}
          promptClassName={isNebulaMode ? "bg-fill-white dark:bg-fill-page-surface-dark" : ""}
          header={
            space_id ? (
              <Header type="spectra-chat" space_id={space_id} spaceName={spaceResponse?.spectra?.title} collaborators={spaceResponse?.spectra?.collaborators || []} hasArtifacts={nebulaMessages.length > lastSeenCount} compactAnswers={compactAnswers} onCompactAnswersChange={changeCompactAnswers} />
            ) : (
              <Header type="spark-chat" hasArtifacts={nebulaMessages.length > lastSeenCount} compactAnswers={compactAnswers} onCompactAnswersChange={changeCompactAnswers} />
            )
          }
          prompt={
            <div className="relative">
              {isNebulaMode && nebulaHasAnswer && (
                <button
                  onClick={() => setNebulaFooterOpen(o => !o)}
                  className="absolute -top-3.5 right-8 z-10 flex items-center justify-center w-7 h-5 rounded-full bg-fill-gray-100 dark:bg-fill-panel-surface hover:bg-fill-gray-200 dark:hover:bg-fill-toolbar-hover transition-colors shadow-sm"
                  aria-label={nebulaFooterOpen ? 'Collapse toolbar' : 'Show toolbar'}
                >
                  <ChevronUp className={`w-3 h-3 text-ink-gray-500 dark:text-ink-gray-400 transition-transform duration-300 ${nebulaFooterOpen ? 'rotate-0' : 'rotate-180'}`} />
                </button>
              )}
            <Prompt
              chat={chat}
              messageLength={messages.length}
              generateStatus={generateStatus}
              setGenerateStatus={setGenerateStatus}
              setRegenerateIndex={setRegenerateIndex}
              setIsNewDrag={setIsNewDrag}
              isNewDrag={isNewDrag}
              send={() => {
                return handleSend({
                  chat_id,
                  sendMessageMutation,
                  messages,
                  setRegenerateIndex,
                  setIsLatestMessageLoading,
                  setAddChatDisabled,
                  user,
                  orgId,
                  files,
                  modeRef,
                  apiUrl,
                  generateStatus,
                  setGenerateStatus,
                  space: spaceResponse?.spectra,
                  chat,
                  onApprovalCard: handleApprovalCardReceived,
                  clearAllApprovals: handleClearAllApprovals,
                  queryClient,
                  deviceId: selectedDeviceId,
                });
              }}
            />
            </div>
          }
        >
          {hasEnteredNebula && (
            /* Nebula view — kept mounted (hidden when inactive) so switching
               back and forth is instant; preview is suspended via isVisible. */
            <div className={isNebulaMode ? "contents" : "hidden"}>
            {(() => {
              const resolvedIndex = nebulaActiveIndex ?? (nebulaMessages.length - 1);
              const activeMsg = nebulaMessages?.[resolvedIndex];
              const visibleNebulaAnswers = activeMsg?.answers?.filter(
                (answer) => answer?.id && !answer.awaiting_approval
              ) || [];
              const totalAnswers = visibleNebulaAnswers.length;
              const selectedAnswerId = selectedAnswerByPrompt[activeMsg?.id];
              const selectedAnswerIndex = activeMsg?.answers?.findIndex(a => a.id === selectedAnswerId) ?? -1;
              const fallbackAnswerId = getDisplayAnswerId(activeMsg?.answers);
              const fallbackAnswerIndex = activeMsg?.answers?.findIndex(a => a.id === fallbackAnswerId) ?? -1;
              const resolvedAnswerIndex = selectedAnswerIndex >= 0 ? selectedAnswerIndex : Math.max(0, fallbackAnswerIndex);
              const activeAnswer = activeMsg?.answers?.[resolvedAnswerIndex];
              const artifact = activeAnswer ? parseNebulaResponse(activeAnswer.content) : null;
              const prevMsg = nebulaMessages?.[resolvedIndex - 1];
              const prevAnswerId = selectedAnswerByPrompt[prevMsg?.id] || getDisplayAnswerId(prevMsg?.answers);
              const prevAnswer = prevMsg?.answers?.find(a => a.id === prevAnswerId);
              const previousArtifact = prevAnswer ? parseNebulaResponse(prevAnswer.content) : null;
              // True from the moment optimistic answer is added (empty content) through streaming end
              const isNebulaGenerating = !isTerminalAnswerStatus(activeAnswer?.status) && (
                isLatestMessageLoading || Boolean(activeAnswer && !activeAnswer.content?.trim())
              );
              const shouldShowNebulaActions = !!activeAnswer && shouldShowMessageActions({
                isAnswerDisplayable: !!activeAnswer.content?.trim(),
                hasAnswers: totalAnswers > 0,
                answerStatus: activeAnswer.status,
                isRegenerating: regenerateIndex === resolvedIndex,
                hasSignal: isNebulaGenerating,
              });

              return (
                <div className="flex flex-col h-full group">
                  <NebulaMessageDots
                    messages={nebulaMessages}
                    activeIndex={resolvedIndex}
                    selectedAnswerByPrompt={selectedAnswerByPrompt}
                    onSelectIndex={(i) => { setNebulaActiveIndex(i); setNebulaViewMode("canvas"); }}
                    viewMode={nebulaViewMode}
                    onToggleOverview={() => setNebulaViewMode(v => v === "overview" ? "canvas" : "overview")}
                    onRegenerateFromIndex={(index) => {
                      const targetMsg = nebulaMessages[index];
                      if (!targetMsg) return;
                      handleClearApprovals?.(targetMsg.id);
                      regenerate({
                        id: targetMsg.id,
                        index,
                        messages,
                        regenerateMessageMutation,
                        modeRef,
                        chat_id,
                        orgId,
                        user,
                        files,
                        modelSelected: defaultModel,
                        setIsLatestMessageLoading,
                        setRegenerateIndex,
                        setGenerateStatus,
                        space: spaceResponse?.spectra,
                        chat,
                        onApprovalCard: handleApprovalCardReceived,
                        queryClient,
                      });
                    }}
                  />
                  <NebulaApprovalPanel
                    pendingApprovals={pendingApprovals}
                    messageId={activeMsg?.id}
                    answerId={activeAnswer?.id}
                    onApprove={handleApprovalConfirm}
                    onCancel={handleApprovalCancel}
                    onExpire={handleApprovalExpire}
                  />
                  <div className="flex flex-row flex-1 min-w-0 min-h-0">
                    {/* Overview — kept mounted to prevent iframe remount on toggle */}
                    <div className={`flex-1 min-h-0 ${nebulaViewMode === "overview" ? "flex" : "hidden"}`}>
                      <NebulaOverview
                        messages={nebulaMessages}
                        activeIndex={resolvedIndex}
                        selectedAnswerByPrompt={selectedAnswerByPrompt}
                        isStreaming={isNebulaGenerating}
                        onSelectIndex={(i) => { setNebulaActiveIndex(i); setNebulaViewMode("canvas"); }}
                      />
                    </div>

                    {/* Canvas — kept mounted to preserve editor state */}
                    <div className={`flex flex-row flex-1 min-w-0 min-h-0 overflow-hidden ${nebulaViewMode === "canvas" ? "" : "hidden"}`}>
                      {nebulaViewMode === "canvas" && (
                        <NebulaActivityPanel
                          activityItems={
                            // During streaming or while the snapshot is still holding:
                            // use Zustand live/snapshot items. Hand off to DB only once
                            // the snapshot has been explicitly cleared by the guard above.
                            (nebulaIsActivelyStreaming || nebulaIsCompletedSnapshot)
                              ? nebulaActivityItems
                              : (nebulaDbActivity || nebulaActivityItems)
                          }
                          isStreaming={isNebulaGenerating}
                        />
                      )}
                      <NebulaCanvas
                        artifact={artifact}
                        isGenerating={isNebulaGenerating}
                        activityItems={nebulaActivityItems}
                        previousArtifact={previousArtifact}
                        answerId={activeAnswer?.id ?? null}
                        messageId={nebulaMsgId ?? null}
                        chatOwnerId={spaceOwnerId ?? user?.uid ?? null}
                        rawContent={activeAnswer?.content ?? null}
                        isVisible={isNebulaMode}
                      />
                    </div>
                  </div>
                  {activeAnswer?.status === 'stopped' && (
                    <div
                      className="max-w-4xl w-full mx-auto px-8 py-2 text-xs text-ink-amber-500 dark:text-ink-amber-400"
                      role="status"
                      aria-live="polite"
                    >
                      {activeAnswer.content?.trim()
                        ? "Generation was stopped"
                        : "Generation stopped before a response was produced."}
                    </div>
                  )}
                  {shouldShowNebulaActions && (
                    <div className={`transition-all duration-300 w-full ${nebulaFooterOpen ? 'max-h-24 opacity-100' : 'max-h-0 opacity-0 pointer-events-none'}`}>
                      <div className="max-w-4xl w-full mx-auto px-8 pb-2">
                      <MessageFooter
                        regenFunc={(id, modelSelected) => {
                          handleClearApprovals?.(id);
                          const m = modelSelected || defaultModel;
                          return regenerate({
                            id,
                            index: resolvedIndex,
                            messages,
                            regenerateMessageMutation,
                            modeRef,
                            chat_id,
                            orgId,
                            user,
                            files,
                            modelSelected: m,
                            setIsLatestMessageLoading,
                            setRegenerateIndex,
                            setGenerateStatus,
                            space: spaceResponse?.spectra,
                            chat,
                            onApprovalCard: handleApprovalCardReceived,
                            queryClient,
                            deviceId: selectedDeviceId,
                          });
                        }}
                        chat={activeMsg}
                        handlePrevious={() => {
                          const visibleIndex = visibleNebulaAnswers.findIndex(a => a.id === activeAnswer?.id);
                          const answerId = visibleNebulaAnswers[visibleIndex - 1]?.id;
                          if (answerId) setSelectedAnswer(activeMsg.id, answerId);
                        }}
                        handleNext={() => {
                          const visibleIndex = visibleNebulaAnswers.findIndex(a => a.id === activeAnswer?.id);
                          const answerId = visibleNebulaAnswers[visibleIndex + 1]?.id;
                          if (answerId) setSelectedAnswer(activeMsg.id, answerId);
                        }}
                        disablePrevious={visibleNebulaAnswers[0]?.id === activeAnswer?.id}
                        disableNext={visibleNebulaAnswers[totalAnswers - 1]?.id === activeAnswer?.id}
                        currentIndex={resolvedAnswerIndex}
                        totalAnswers={totalAnswers}
                        content={activeAnswer?.content}
                        files={files}
                        index={resolvedIndex}
                        regenerateIndex={regenerateIndex}
                        setRegenerateIndex={setRegenerateIndex}
                        modelSelected={activeAnswer?.model_id}
                        hideRegenerate={!canRetryAnswer(activeAnswer)}
                        deleteMessage={deleteMessage}
                      />
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
            </div>
          )}

          {/* Chat transcript — kept mounted (hidden when in Nebula) so switching
              back is instant and scroll/render state is preserved. */}
          <div className={isNebulaMode ? "hidden" : "contents"}>
          <ChatMessages
            messages={messages}
            chat={chat}
            user={user}
            orgId={orgId}
            deleteMessage={deleteMessage}
            editFunctionality={editFunctionality}
            regenerateIndex={regenerateIndex}
            setRegenerateIndex={setRegenerateIndex}
            editedIndex={editedIndex}
            setEditedIndex={setEditedIndex}
            generateStatus={generateStatus}
            setGenerateStatus={setGenerateStatus}
            defaultModel={defaultModel}
            files={files}
            modeRef={modeRef}
            setIsLatestMessageLoading={setIsLatestMessageLoading}
            messageContainerRef={messageContainerRef}
            isMessagesRendered={isMessagesRendered}
            setMessagesRendered={setMessagesRendered}
            isDataReady={isDataReady}
            setMessageCount={setMessageCount}
            setIsNewDrag={setIsNewDrag}
            isNewDrag={isNewDrag}
            pendingApprovals={isNebulaMode ? {} : pendingApprovals}
            regenerateMessageMutation={regenerateMessageMutation}
            queryClient={queryClient}
            onApprovalConfirm={handleApprovalConfirm}
            onApprovalCancel={handleApprovalCancel}
            onApprovalExpire={handleApprovalExpire}
            onApprovalCard={handleApprovalCardReceived}
            onClearApprovals={handleClearApprovals}
            compactAnswers={compactAnswers}
            expandedExchangeIds={expandedExchangeIds}
            collapsedExchangeIds={collapsedExchangeIds}
            onExchangeExpandedChange={setExchangeExpanded}
          />
          </div>
        </SparkLayout>
      ) : (
        <></>
      )}

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleOnChange}
        accept={ALLOWED_FILE_ACCEPT_STRING}
      />
    </div>
    </SparkContext.Provider>
  );
};

export default MainContent;
